import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown } from 'lucide-react';
import {
  Search, Building2, Home, MapPin, TrendingUp, Users, Shield, ArrowRight,
  Landmark, Calculator, BarChart3, Newspaper,
  ChevronLeft, ChevronRight, Star, Phone, ExternalLink, ShieldCheck, Handshake, Lock, SlidersHorizontal, RotateCcw, Filter,
  LucideIcon, LandPlot, Briefcase, Image as ImageIcon, FileText, Award, Map,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import heroBg from '@/assets/hero-bg.jpg';
import homeAdvisorAiIcon from '@/assets/home-advisor-ai.png';
import avmIcon from '@/assets/avm.png';
import newProjectsIcon from '@/assets/new-projects.png';
import newProjectsTabIcon from '@/assets/projects-icon.png.asset.json';
import rentTabIcon from '@/assets/rent-icon.png.asset.json';
import societyMapsIcon from '@/assets/society-maps.png';
import areaGuidesIcon from '@/assets/area-guides.png';
import costCalculatorIcon from '@/assets/cost-calculator.png';
import ethioPropertyInvestIcon from '@/assets/ethio-property-invest.png';
import { optimizedImageUrl, optimizedSrcSet } from '@/lib/imageOptimizer';
import {
  useHero, useCities, useExploreTiles, useLatestNews, useLatestBlog, useLocalize,
} from '@/hooks/useLandingContent';
import { useNeighborhoodsByCity } from '@/hooks/useNeighborhoods';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice, formatPriceRange } from '@/lib/formatPrice';
import { CurrencySwitcher } from '@/components/CurrencySwitcher';
import { FeedStoriesRow } from '@/components/property/FeedStoriesRow';
import { FavoritePropertiesRow } from '@/components/landing/FavoritePropertiesRow';
import { ViewedProjectsRow } from '@/components/landing/ViewedProjectsRow';
import { ViewedPropertiesRow } from '@/components/landing/ViewedPropertiesRow';
import { TitaniumAgencies } from '@/components/landing/TitaniumAgencies';
import { TrendingProjectsTwoRow } from '@/components/landing/TrendingProjectsTwoRow';
import { CommunitySection } from '@/components/landing/CommunitySection';

import { PropertyScrollRow } from '@/components/landing/PropertyScrollRow';
import { PropertyCard } from '@/components/property/PropertyCard';
import { RecommendedPropertyCard } from '@/components/property/RecommendedPropertyCard';
import { NewPropertiesRow } from '@/components/landing/NewPropertiesRow';
import { MobileBrowsePropertiesCard } from '@/components/landing/MobileBrowsePropertiesCard';
import { HeroPropertyTypePopover, HeroCategory } from '@/components/landing/HeroPropertyTypePopover';
import { MobileNewsScroll } from '@/components/landing/MobileNewsScroll';
import { MobilePostAdCTA } from '@/components/landing/MobilePostAdCTA';
import { MobileAgenciesRow } from '@/components/landing/MobileAgenciesRow';
import { MobileTitaniumAgencies } from '@/components/landing/MobileTitaniumAgencies';
import { useIsMobile } from '@/hooks/use-mobile';
import { BannerSlot } from '@/components/banners/BannerSlot';
import { HeroSavedSearches } from '@/components/landing/HeroSavedSearches';

// Map icon names stored in `explore_tiles.icon_name` to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  Landmark, Calculator, BarChart3, MapPin, Map, Building2, Home, TrendingUp, Users,
  Shield, LandPlot, Briefcase, FileText, Award, Newspaper, Image: ImageIcon,
};

function RangePopover({ minValue, maxValue, setMin, setMax, minOptions, maxOptions, format, unit }: {
  minValue: string; maxValue: string;
  setMin: (v: string) => void; setMax: (v: string) => void;
  minOptions: string[]; maxOptions: string[];
  format: (v: string) => string;
  unit?: string;
}) {
  const label = (!minValue && !maxValue)
    ? '0 to Any'
    : `${minValue ? format(minValue) : '0'} to ${maxValue ? format(maxValue) : 'Any'}`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="w-full h-10 px-3 flex items-center justify-between border border-border/60 rounded-md text-xs bg-background hover:bg-accent/30">
          <span className="truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(20rem,calc(100vw-1.5rem))] p-3" align="start">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="text-[10px] font-semibold text-muted-foreground mb-1">MIN {unit ? `(${unit})` : ''}</div>
            <Input
              type="number"
              inputMode="numeric"
              placeholder="0"
              value={minValue}
              onChange={(e) => setMin(e.target.value.replace(/[^0-9.]/g, ''))}
              className="h-9 text-xs"
            />
          </div>
          <div>
            <div className="text-[10px] font-semibold text-muted-foreground mb-1">MAX {unit ? `(${unit})` : ''}</div>
            <Input
              type="number"
              inputMode="numeric"
              placeholder="Any"
              value={maxValue}
              onChange={(e) => setMax(e.target.value.replace(/[^0-9.]/g, ''))}
              className="h-9 text-xs"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-semibold text-muted-foreground mb-1">PRESETS</div>
            <div className="max-h-44 overflow-y-auto border rounded-md">
              <button type="button" onClick={() => setMin('')} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-accent ${!minValue ? 'bg-primary text-primary-foreground' : ''}`}>0</button>
              {minOptions.map(v => (
                <button key={v} type="button" onClick={() => setMin(v)} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-accent ${minValue === v ? 'bg-primary text-primary-foreground' : ''}`}>{format(v)}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-muted-foreground mb-1">PRESETS</div>
            <div className="max-h-44 overflow-y-auto border rounded-md">
              <button type="button" onClick={() => setMax('')} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-accent ${!maxValue ? 'bg-primary text-primary-foreground' : ''}`}>Any</button>
              {maxOptions.map(v => (
                <button key={v} type="button" onClick={() => setMax(v)} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-accent ${maxValue === v ? 'bg-primary text-primary-foreground' : ''}`}>{format(v)}</button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Index() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localize = useLocalize();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'buy' | 'rent' | 'projects'>('buy');
  const [selectedCity, setSelectedCity] = useState('Addis Ababa');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('all');
  const [heroCategory, setHeroCategory] = useState<HeroCategory>('homes');
  const [heroSubtype, setHeroSubtype] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minArea, setMinArea] = useState('');
  const [maxArea, setMaxArea] = useState('');
  const [beds, setBeds] = useState('all');
  const [baths, setBaths] = useState('all');
  // Advanced filters
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advVerified, setAdvVerified] = useState(false);
  const [advSuperHot, setAdvSuperHot] = useState(false);
  const [advOccupancy, setAdvOccupancy] = useState('all');
  const [advOwnership, setAdvOwnership] = useState('all');
  const [advDays, setAdvDays] = useState('all');
  const [advPaymentTypes, setAdvPaymentTypes] = useState<string[]>([]);
  const [advListedByAgency, setAdvListedByAgency] = useState(false);
  // Project-specific filters
  const [projectType, setProjectType] = useState('');
  const [projectMinPrice, setProjectMinPrice] = useState('');
  const [projectMaxPrice, setProjectMaxPrice] = useState('');
  const [projectMinArea, setProjectMinArea] = useState('');
  const [projectMaxArea, setProjectMaxArea] = useState('');
  const [developerQuery, setDeveloperQuery] = useState('');
  const [projectPaymentTypes, setProjectPaymentTypes] = useState<string[]>([]);
  const [projectStatus, setProjectStatus] = useState('all');
  const agencyScrollRef = useRef<HTMLDivElement>(null);
  const projectScrollRef = useRef<HTMLDivElement>(null);
  const { displayCurrency, rate } = useCurrency();
  const isMobile = useIsMobile();

  // CMS-driven content
  const { data: hero } = useHero();
  const { data: citiesData } = useCities();
  const { data: exploreTiles } = useExploreTiles();
  const { data: latestNews } = useLatestNews(3);
  const { data: blogPostsData } = useLatestBlog(3);

  const cityNames = (citiesData && citiesData.length > 0)
    ? citiesData.map(c => c.name)
    : ['Addis Ababa'];

  const selectedCityId = citiesData?.find(c => c.name === selectedCity)?.id;
  const { data: neighborhoods = [] } = useNeighborhoodsByCity(selectedCityId);

  // Keep selected city valid as the list loads
  useEffect(() => {
    if (cityNames.length > 0 && !cityNames.includes(selectedCity)) {
      setSelectedCity(cityNames[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citiesData]);

  // Reset neighborhood when city changes
  useEffect(() => {
    setSelectedNeighborhood('all');
  }, [selectedCity]);


  // Dynamic data queries
  const { data: approvedProjects } = useQuery({
    queryKey: ['landing-projects'],
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('*, project_images(url, is_primary), developer_profiles(company_name, phone, whatsapp)')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false })
        .limit(8);
      return data || [];
    },
  });

  const { data: verifiedAgencies } = useQuery({
    queryKey: ['landing-agencies'],
    queryFn: async () => {
      const { data } = await supabase
        .from('agencies')
        .select('*')
        .eq('is_verified', true)
        .limit(10);
      return data || [];
    },
  });

  const { data: newProperties } = useQuery({
    queryKey: ['landing-new-properties'],
    queryFn: async () => {
      // Include both freshly created listings AND boosted/refreshed ones
      // (featured first, then most recently updated).
      const { data } = await supabase
        .from('properties')
        .select('*, property_images(url, is_primary)')
        .eq('approval_status', 'approved')
        .eq('status', 'active')
        .order('is_featured', { ascending: false })
        .order('updated_at', { ascending: false })
        .limit(12);
      return data || [];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['landing-stats'],
    queryFn: async () => {
      const [propRes, profileRes, agencyRes] = await Promise.all([
        supabase.from('properties').select('id', { count: 'exact', head: true }).eq('approval_status', 'approved').eq('status', 'active'),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('agencies').select('id', { count: 'exact', head: true }).eq('is_verified', true),
      ]);
      return {
        properties: propRes.count || 0,
        users: profileRes.count || 0,
        agencies: agencyRes.count || 0,
      };
    },
  });

  const handleSearch = () => {
    if (activeTab === 'projects') {
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (developerQuery) params.set('developer', developerQuery);
      if (selectedCity) params.set('city', selectedCity);
      if (selectedNeighborhood && selectedNeighborhood !== 'all') params.set('neighborhood', selectedNeighborhood);
      if (projectType) params.set('project_type', projectType);
      if (projectMinPrice) params.set('minPrice', projectMinPrice);
      if (projectMaxPrice) params.set('maxPrice', projectMaxPrice);
      if (projectMinArea) params.set('minArea', projectMinArea);
      if (projectMaxArea) params.set('maxArea', projectMaxArea);
      if (projectPaymentTypes.length) params.set('payment_types', projectPaymentTypes.join(','));
      if (projectStatus && projectStatus !== 'all') params.set('status', projectStatus);
      navigate(`/projects?${params.toString()}`);
      return;
    }
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCity) params.set('city', selectedCity);
    if (selectedNeighborhood && selectedNeighborhood !== 'all') params.set('neighborhood', selectedNeighborhood);
    if (heroSubtype) params.set('type', heroSubtype);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (minArea) params.set('minArea', minArea);
    if (maxArea) params.set('maxArea', maxArea);
    if (heroCategory === 'homes' && beds !== 'all') params.set('beds', beds);
    if (heroCategory === 'homes' && baths !== 'all') params.set('baths', baths);
    // Advanced
    if (advVerified) params.set('verified', '1');
    if (advSuperHot) params.set('super_hot', '1');
    if (advOccupancy !== 'all') params.set('occupancy_status', advOccupancy);
    if (advOwnership !== 'all') params.set('ownership_status', advOwnership);
    if (advDays !== 'all') params.set('days_on_platform', advDays);
    if (advPaymentTypes.length) params.set('payment_types', advPaymentTypes.join(','));
    if (advListedByAgency) params.set('listed_by_agency', '1');

    let basePath: string;
    if (heroCategory === 'plots') basePath = '/plots';
    else if (heroCategory === 'commercial') basePath = '/commercial';
    else basePath = `/${activeTab}`;
    navigate(`${basePath}?${params.toString()}`);
  };

  const scroll = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) {
      ref.current.scrollBy({ left: direction === 'left' ? -300 : 300, behavior: 'smooth' });
    }
  };

  const getName = (val: any) => (typeof val === 'object' ? val?.en || '' : val || '');

  const statItems = [
    { icon: Building2, value: stats ? `${stats.properties.toLocaleString('en-US')}+` : '...', label: 'Properties Listed' },
    { icon: Users, value: stats ? `${stats.users.toLocaleString('en-US')}+` : '...', label: 'Registered Users' },
    { icon: MapPin, value: citiesData ? `${citiesData.length}+` : '...', label: 'Cities Covered' },
    { icon: TrendingUp, value: stats ? `${stats.agencies}+` : '...', label: 'Verified Agencies' },
  ];

  // Fallback agencies if none verified yet (kept minimal until DB populated)
  const fallbackAgencies: any[] = [];

  const fallbackProjects: any[] = [];

  const displayAgencies = verifiedAgencies?.length ? verifiedAgencies : fallbackAgencies;
  const displayProjects = approvedProjects?.length ? approvedProjects : fallbackProjects;

  const homepageJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Ethio Real Estates",
      url: "https://ethio-real-estate-hub.lovable.app",
      logo: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/7f169d66-3955-46a0-86f4-b65736a568fd",
      description: "Ethiopia's leading real estate platform for buying, renting, and investing in property.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: "https://ethio-real-estate-hub.lovable.app",
      name: "Ethio Real Estates",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://ethio-real-estate-hub.lovable.app/buy?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Ethio Real Estates — Buy, Rent & Invest in Ethiopian Property"
        description="Search houses, apartments, villas, land and new projects for sale or rent in Addis Ababa, Adama, Hawassa and across Ethiopia."
        canonical="/"
        jsonLd={homepageJsonLd}
      />
      {/* Hero Section */}
      <section className="relative">
        <div className="relative">
          <img
            src={heroBg}
            alt="Addis Ababa cityscape"
            className="absolute inset-0 w-full h-full object-cover"
            width={1920}
            height={800}
            loading="eager"
            decoding="async"
            // @ts-expect-error - fetchpriority is a valid HTML attribute, React 18 types lag behind
            fetchpriority="high"
          />
          <div className="absolute inset-x-0 bottom-0 h-[15%] bg-gradient-to-t from-white via-white/80 to-transparent" />
          <div className="relative z-10 container pt-6 md:pt-10 pb-28 md:pb-36">
            {/* Ad Ops — optional banner above the hero heading. Renders nothing if no active campaign. */}
            <div className="mb-6 flex justify-center">
              <BannerSlot
                placementCode="homepage_hero_top"
                page="homepage"
                className="w-full max-w-4xl [&>div>div]:mx-auto [&_a]:mx-auto [&_img]:mx-auto"
              />
            </div>
            <div className="max-w-3xl">
              <h1 className="font-display text-3xl sm:text-5xl md:text-[56px] font-bold leading-[1.05] tracking-tight text-foreground">
                Find Your Perfect
                <br />
                <span className="text-primary">Property in Ethiopia</span>
              </h1>
              <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-md">
                Search thousands of verified properties<br className="hidden sm:block" />
                to buy, rent or invest with confidence.
              </p>
            </div>

            {/* Centered search block */}
            <div className="mt-8 max-w-4xl mx-auto">
              {/* Tabs */}
              <div className="flex justify-center gap-1.5">
                {([
                  { key: 'buy', label: 'BUY', icon: Home, img: null as string | null },
                  { key: 'rent', label: 'RENT', icon: Building2, img: rentTabIcon.url },
                  { key: 'projects', label: 'PROJECTS', icon: Briefcase, img: newProjectsTabIcon.url },
                ] as const).map(({ key, label, icon: Icon, img }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key as any)}
                    className={`flex items-center gap-2 px-6 sm:px-10 py-2.5 rounded-t-md text-xs sm:text-sm font-bold tracking-wider transition-all ${
                      activeTab === key
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'bg-slate-200/80 text-slate-700 hover:bg-slate-300/80'
                    }`}
                  >
                    {img ? (
                      <img src={img} alt="" className={`h-9 w-9 object-contain contrast-125 ${activeTab === key ? 'brightness-0 invert drop-shadow-[0_0_1.5px_rgba(255,255,255,0.9)]' : 'drop-shadow-[0_0_1.5px_rgba(0,0,0,0.6)]'}`} />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                    {label}
                  </button>
                ))}
              </div>


              {/* Search Card */}
              <div className="bg-white rounded-md shadow-2xl p-4 md:p-5 border border-border/40">
                <div className="flex flex-col md:flex-row gap-2">
                  <div className="md:w-[220px]">
                    <Select value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger aria-label="Select city" className="h-12 border-border/60 text-sm rounded-md">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-primary shrink-0" />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Addis Ababa">Addis Ababa</SelectItem>
                        {cityNames.filter(c => c !== 'Addis Ababa').map(city => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:w-[200px]">
                    <Select
                      value={selectedNeighborhood}
                      onValueChange={setSelectedNeighborhood}
                      disabled={!neighborhoods || neighborhoods.length === 0}
                    >
                      <SelectTrigger aria-label="Select neighborhood" className="h-12 border-border/60 text-xs rounded-md whitespace-nowrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <MapPin className="h-4 w-4 text-primary shrink-0" />
                          <SelectValue placeholder={neighborhoods.length === 0 ? 'No neighborhoods' : 'All neighborhoods'} />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All neighborhoods</SelectItem>
                        {neighborhoods.map((n: any) => (
                          <SelectItem key={n.id} value={n.slug}>{n.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by area, neighborhood, or keyword..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSearch()}
                      className="pl-10 h-12 border-border/60 text-sm rounded-md"
                    />
                  </div>
                  <Button onClick={handleSearch} className="h-12 px-10 font-bold text-sm tracking-wider rounded-md">
                    SEARCH
                  </Button>
                </div>

                {activeTab !== 'projects' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mt-3">
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Property Type</label>
                      <HeroPropertyTypePopover
                        category={heroCategory}
                        setCategory={setHeroCategory}
                        subtype={heroSubtype}
                        setSubtype={setHeroSubtype}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Area (m²)</label>
                      <RangePopover
                        minValue={minArea}
                        maxValue={maxArea}
                        setMin={setMinArea}
                        setMax={setMaxArea}
                        minOptions={['50','80','100','150','200','300','500','1000']}
                        maxOptions={['100','150','200','300','500','1000','2000','5000']}
                        format={(v) => `${Number(v).toLocaleString('en-US')} m²`}
                        unit="m²"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Price ({displayCurrency === 'ETB' ? 'Birr' : displayCurrency})</label>
                      <RangePopover
                        minValue={minPrice}
                        maxValue={maxPrice}
                        setMin={setMinPrice}
                        setMax={setMaxPrice}
                        minOptions={activeTab === 'rent'
                          ? ['5000','10000','15000','25000','40000','60000','100000','200000']
                          : ['2000000','5000000','10000000','20000000','40000000','75000000']}
                        maxOptions={activeTab === 'rent'
                          ? ['10000','20000','40000','75000','150000','300000','500000']
                          : ['5000000','10000000','20000000','50000000','100000000','250000000']}
                        format={(v) => Number(v).toLocaleString('en-US')}
                        unit={displayCurrency === 'ETB' ? 'Birr' : displayCurrency}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Beds</label>
                      <Select value={beds} onValueChange={setBeds}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          {['1','2','3','4','5','6'].map(n => <SelectItem key={n} value={n}>{n === '6' ? '6+' : n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Baths</label>
                      <Select value={baths} onValueChange={setBaths}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          {['1','2','3','4','5','6'].map(n => <SelectItem key={n} value={n}>{n === '6' ? '6+' : n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {activeTab === 'projects' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mt-3">
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Property Type</label>
                      <Select value={projectType} onValueChange={setProjectType}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          <SelectItem value="studio">Studio</SelectItem>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="modern_villa">Modern Villa</SelectItem>
                          <SelectItem value="old_villa">Old Villa</SelectItem>
                          <SelectItem value="l_shape_villa">L-Shape Villa</SelectItem>
                          <SelectItem value="townhouse">Townhouse</SelectItem>
                          <SelectItem value="penthouse">Penthouse</SelectItem>
                          <SelectItem value="condominium">Condominium</SelectItem>
                          <SelectItem value="villa">Villa</SelectItem>
                          <SelectItem value="flat">Flat</SelectItem>
                          <SelectItem value="commercial_spaces">Commercial Spaces</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Area Range (m²)</label>
                      <RangePopover
                        minValue={projectMinArea}
                        maxValue={projectMaxArea}
                        setMin={setProjectMinArea}
                        setMax={setProjectMaxArea}
                        minOptions={['50','80','100','150','200','300','500','1000']}
                        maxOptions={['100','150','200','300','500','1000','2000','5000']}
                        format={(v) => `${Number(v).toLocaleString('en-US')} m²`}
                        unit="m²"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Budget Range ({displayCurrency === 'ETB' ? 'Birr' : displayCurrency})</label>
                      <RangePopover
                        minValue={projectMinPrice}
                        maxValue={projectMaxPrice}
                        setMin={setProjectMinPrice}
                        setMax={setProjectMaxPrice}
                        minOptions={['2000000','5000000','10000000','20000000','40000000','75000000']}
                        maxOptions={['5000000','10000000','20000000','50000000','100000000','250000000']}
                        format={(v) => Number(v).toLocaleString('en-US')}
                        unit={displayCurrency === 'ETB' ? 'Birr' : displayCurrency}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Payment</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="w-full h-10 px-3 flex items-center justify-between border border-border/60 rounded-md text-xs bg-background hover:bg-accent/30">
                            <span className="truncate">{projectPaymentTypes.length ? `${projectPaymentTypes.length} selected` : 'Any'}</span>
                            <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2" align="start">
                          {[
                            { v: 'cash', l: 'Cash' },
                            { v: 'installments', l: 'Installment' },
                            { v: 'bank', l: 'Bank Option' },
                          ].map(opt => {
                            const checked = projectPaymentTypes.includes(opt.v);
                            return (
                              <label key={opt.v} className="flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => setProjectPaymentTypes(prev => checked ? prev.filter(x => x !== opt.v) : [...prev, opt.v])}
                                />
                                {opt.l}
                              </label>
                            );
                          })}
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Project Progress</label>
                      <Select value={projectStatus} onValueChange={setProjectStatus}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          <SelectItem value="upcoming">Off-Plan</SelectItem>
                          <SelectItem value="under_construction">Under Construction</SelectItem>
                          <SelectItem value="ready">Ready</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between flex-wrap gap-2 mt-3 pt-3 border-t border-border/30">
                  <button
                    onClick={() => setAdvancedOpen(o => !o)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                    aria-expanded={advancedOpen}
                  >
                    {advancedOpen ? (
                      <>
                        See Less
                        <ChevronDown className="h-3.5 w-3.5 transition-transform rotate-180" />
                      </>
                    ) : (
                      <>
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        Advanced Search
                        <ChevronDown className="h-3.5 w-3.5 transition-transform" />
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-4">
                    <HeroSavedSearches
                      currentFilters={{
                        activeTab, searchQuery, selectedCity, selectedNeighborhood,
                        heroCategory, heroSubtype,
                        minPrice, maxPrice, minArea, maxArea, beds, baths,
                        advVerified, advSuperHot, advOccupancy, advOwnership,
                        advDays, advPaymentTypes, advListedByAgency,
                        projectType, projectMinPrice, projectMaxPrice,
                        projectMinArea, projectMaxArea, developerQuery,
                        projectPaymentTypes, projectStatus,
                      }}
                      onLoad={(f) => {
                        if (f.activeTab) setActiveTab(f.activeTab);
                        if (typeof f.searchQuery === 'string') setSearchQuery(f.searchQuery);
                        if (f.selectedCity) setSelectedCity(f.selectedCity);
                        if (f.selectedNeighborhood) setSelectedNeighborhood(f.selectedNeighborhood);
                        if (f.heroCategory) setHeroCategory(f.heroCategory);
                        if (typeof f.heroSubtype === 'string') setHeroSubtype(f.heroSubtype);
                        setMinPrice(f.minPrice || ''); setMaxPrice(f.maxPrice || '');
                        setMinArea(f.minArea || ''); setMaxArea(f.maxArea || '');
                        setBeds(f.beds || 'all'); setBaths(f.baths || 'all');
                        setAdvVerified(!!f.advVerified); setAdvSuperHot(!!f.advSuperHot);
                        setAdvOccupancy(f.advOccupancy || 'all'); setAdvOwnership(f.advOwnership || 'all');
                        setAdvDays(f.advDays || 'all');
                        setAdvPaymentTypes(Array.isArray(f.advPaymentTypes) ? f.advPaymentTypes : []);
                        setAdvListedByAgency(!!f.advListedByAgency);
                        setProjectType(f.projectType || '');
                        setProjectMinPrice(f.projectMinPrice || ''); setProjectMaxPrice(f.projectMaxPrice || '');
                        setProjectMinArea(f.projectMinArea || ''); setProjectMaxArea(f.projectMaxArea || '');
                        setDeveloperQuery(f.developerQuery || '');
                        setProjectPaymentTypes(Array.isArray(f.projectPaymentTypes) ? f.projectPaymentTypes : []);
                        setProjectStatus(f.projectStatus || 'all');
                        setAdvancedOpen(true);
                      }}
                    />
                    <button
                      className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCity(cityNames[0] || 'Addis Ababa');
                        setHeroSubtype('');
                        setMinPrice(''); setMaxPrice('');
                        setMinArea(''); setMaxArea('');
                        setBeds('all');
                        setBaths('all');
                        setAdvVerified(false); setAdvSuperHot(false);
                        setAdvOccupancy('all'); setAdvOwnership('all');
                        setAdvDays('all'); setAdvPaymentTypes([]);
                        setAdvListedByAgency(false);
                        setProjectType('');
                        setProjectMinPrice(''); setProjectMaxPrice('');
                        setProjectMinArea(''); setProjectMaxArea('');
                        setDeveloperQuery('');
                        setProjectPaymentTypes([]);
                        setProjectStatus('all');
                      }}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset
                    </button>
                  </div>
                </div>

                {advancedOpen && activeTab !== 'projects' && (
                  <div className="mt-3 pt-3 border-t border-border/30 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Occupancy Status</label>
                      <Select value={advOccupancy} onValueChange={setAdvOccupancy}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          <SelectItem value="occupied">Occupied</SelectItem>
                          <SelectItem value="vacant">Vacant</SelectItem>
                          <SelectItem value="not_specified">Not specified</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Ownership Status</label>
                      <Select value={advOwnership} onValueChange={setAdvOwnership}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          <SelectItem value="freehold">Freehold</SelectItem>
                          <SelectItem value="leasehold">Leasehold</SelectItem>
                          <SelectItem value="not_specified">Not specified</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Days on Platform</label>
                      <Select value={advDays} onValueChange={setAdvDays}>
                        <SelectTrigger className="h-10 border-border/60 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any</SelectItem>
                          <SelectItem value="7">1 week</SelectItem>
                          <SelectItem value="14">2 weeks</SelectItem>
                          <SelectItem value="21">3 weeks</SelectItem>
                          <SelectItem value="30">1 month</SelectItem>
                          <SelectItem value="60">2 months</SelectItem>
                          <SelectItem value="90">90 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Payment Type</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="w-full h-10 px-3 flex items-center justify-between border border-border/60 rounded-md text-xs bg-background hover:bg-accent/30">
                            <span className="truncate">{advPaymentTypes.length ? `${advPaymentTypes.length} selected` : 'Any'}</span>
                            <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2" align="start">
                          {[
                            { v: 'cash', l: 'Cash' },
                            { v: 'installments', l: 'Installments' },
                            { v: 'bank', l: 'Bank Options' },
                          ].map(opt => {
                            const checked = advPaymentTypes.includes(opt.v);
                            return (
                              <label key={opt.v} className="flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => setAdvPaymentTypes(prev => checked ? prev.filter(x => x !== opt.v) : [...prev, opt.v])}
                                />
                                {opt.l}
                              </label>
                            );
                          })}
                        </PopoverContent>
                      </Popover>
                    </div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer select-none mt-2">
                      <input type="checkbox" checked={advVerified} onChange={e => setAdvVerified(e.target.checked)} />
                      Verified
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer select-none mt-2">
                      <input type="checkbox" checked={advSuperHot} onChange={e => setAdvSuperHot(e.target.checked)} />
                      Super Hot
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer select-none mt-2">
                      <input type="checkbox" checked={advListedByAgency} onChange={e => setAdvListedByAgency(e.target.checked)} />
                      Listed by Agencies
                    </label>
                  </div>
                )}

                {advancedOpen && activeTab === 'projects' && (
                  <div className="mt-3 pt-3 border-t border-border/30 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Project Title</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Select Projects"
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSearch()}
                          className="pl-10 h-10 border-border/60 text-sm rounded-md"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-muted-foreground mb-1 block pl-1">Developer Title</label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Select Developers"
                          value={developerQuery}
                          onChange={e => setDeveloperQuery(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSearch()}
                          className="pl-10 h-10 border-border/60 text-sm rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>




        {/* Trust Strip — overlapping hero bottom */}
        <div className="container -mt-16 md:-mt-20 relative z-20">
          <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl border border-border/30 px-4 md:px-8 py-5 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-2 divide-y md:divide-y-0 md:divide-x divide-border/40">

            {[
              { icon: ShieldCheck, title: 'Verified Listings', desc: 'All properties are verified and trusted' },
              { icon: MapPin, title: 'Prime Locations', desc: 'Find properties in the best neighborhoods' },
              { icon: Handshake, title: 'Expert Support', desc: 'Get help from our local property experts' },
              { icon: Lock, title: 'Secure & Easy', desc: 'Safe transactions and hassle-free process' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3 px-2 md:px-4 pt-4 md:pt-0 first:pt-0">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-primary" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-foreground leading-tight">{title}</div>
                  <div className="text-[11px] md:text-xs text-muted-foreground leading-snug mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {isMobile ? (
        <>
          {/* Mobile: streamlined Zameen-style layout in our brand colors */}
          <MobileBrowsePropertiesCard />
          <div className="px-3 pt-4">
            <BannerSlot placementCode="homepage_top_mobile" page="homepage" />
          </div>
          <FeedStoriesRow />

          <ViewedPropertiesRow />
          <MobilePostAdCTA />
          <NewPropertiesRow properties={(newProperties || []).slice(0, 12)} title="New Properties" />
          <div className="px-3 py-3">
            <BannerSlot placementCode="homepage_middle_mobile" page="homepage" />
          </div>
          <MobileTitaniumAgencies />
          <div className="px-3 py-4"><div className="border-highlight border-highlight-border rounded-2xl p-1.5 bg-highlight">
            <TrendingProjectsTwoRow projects={displayProjects.slice(0, 8)} />
          </div></div>
          <MobileNewsScroll />
        </>
      ) : (
        <>
          {/* Stories */}
          <FeedStoriesRow />

          {/* Browse Properties */}
          <section className="recommended-section bg-background"><div className="container py-6"><div className="border-highlight border-highlight-border rounded-2xl p-4 md:p-6 bg-highlight">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl md:text-2xl font-bold">Recommended for You</h2>
              <Button variant="link" className="text-primary hover:text-primary active:text-primary hover:bg-transparent active:bg-transparent text-sm font-semibold p-0" onClick={() => navigate('/buy')}>
                View All Properties <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
            {newProperties && newProperties.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {newProperties.slice(0, 10).map((property: any) => (
                  <RecommendedPropertyCard key={property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">No new properties yet.</div>
            )}
          </div></div></section>

          {/* Ad Ops — long banner above Explore More */}
          <section className="container py-0 flex justify-center">

            <BannerSlot placementCode="homepage_above_explore" page="homepage" className="w-full flex justify-center [&>div>div]:mx-auto [&_a]:mx-auto [&_img]:mx-auto" />
          </section>


          {/* Explore More */}
          <section className="container py-8">
            <h2 className="font-display text-lg md:text-xl font-bold mb-6">Explore More</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {(exploreTiles || []).map(tile => {
                const Icon = ICON_MAP[tile.icon_name] || Landmark;
                const customIcon: Record<string, { src: string; alt: string }> = {
                  HomeAdvisorAI: { src: homeAdvisorAiIcon, alt: 'HomeAdvisor AI' },
                  AVM: { src: avmIcon, alt: 'Automated Valuation Models' },
                  NewProjects: { src: newProjectsIcon, alt: 'New Projects' },
                  SocietyMaps: { src: societyMapsIcon, alt: 'Society Maps' },
                  AreaGuides: { src: areaGuidesIcon, alt: 'Area Guides' },
                  CostCalculator: { src: costCalculatorIcon, alt: 'Cost Calculator' },
                  EthioPropertyInvest: { src: ethioPropertyInvestIcon, alt: 'Ethio Property Invest' },
                };
                const custom = customIcon[tile.icon_name];
                return (
                  <button key={tile.id} onClick={() => navigate(tile.link_url)}
                    className="group flex items-start gap-2.5 sm:gap-3.5 p-3 sm:p-4 rounded-xl hover:shadow-md transition-all text-left">
                    <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl ${tile.background_class || 'bg-amber-50'} flex items-center justify-center shrink-0 overflow-hidden`}>
                      {custom ? (
                        <img src={custom.src} alt={custom.alt} className="h-full w-full object-contain" />
                      ) : (
                        <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${tile.color_class || 'text-amber-600'}`} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-xs sm:text-sm leading-tight">{localize(tile.title)}</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-snug line-clamp-2">{localize(tile.description)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <FavoritePropertiesRow />
          <ViewedPropertiesRow />
          <ViewedProjectsRow />
          <TitaniumAgencies />

          {/* Ad Ops — long banner between Agencies and Trending Projects */}
          <section className="container py-0 flex justify-center">
            <BannerSlot placementCode="homepage_above_agencies" page="homepage" className="w-full flex justify-center [&>div>div]:mx-auto [&_a]:mx-auto [&_img]:mx-auto" />
          </section>


          <section className="bg-background"><div className="container py-12"><div className="border-highlight border-highlight-border rounded-2xl p-1.5 md:p-2 bg-highlight">
            <TrendingProjectsTwoRow projects={displayProjects} />
          </div></div></section>
          <CommunitySection />
          
        </>
      )}

    </Layout>
  );
}
